import React from 'react';
import {
  PieChart,
  Pie,
  Cell,
  ResponsiveContainer,
  Tooltip,
  Legend,
} from 'recharts';

interface DonutChartProps {
  data: Array<{ name: string; value: number; color?: string }>;
  title?: string;
  height?: number;
  colors?: string[];
  showTooltip?: boolean;
  showLegend?: boolean;
  showPercentage?: boolean;
  centerLabel?: string;
  centerValue?: string | number;
  formatValue?: (value: number) => string;
}

const DEFAULT_COLORS = [
  '#2563eb',
  '#16a34a',
  '#dc2626',
  '#d97706',
  '#7c3aed',
  '#0891b2',
  '#c026d3',
  '#65a30d',
];

export function DonutChart({
  data,
  title,
  height = 300,
  colors = DEFAULT_COLORS,
  showTooltip = true,
  showLegend = true,
  showPercentage = true,
  centerLabel,
  centerValue,
  formatValue,
}: DonutChartProps) {
  const total = data.reduce((sum, item) => sum + item.value, 0);

  const formatTooltipValue = (value: number) => {
    if (formatValue) return formatValue(value);
    return value.toLocaleString();
  };

  const CustomTooltip = ({ active, payload }: any) => {
    if (active && payload && payload.length) {
      const item = payload[0].payload;
      const percentage = ((item.value / total) * 100).toFixed(1);
      return (
        <div
          style={{
            backgroundColor: 'var(--card)',
            border: '1px solid var(--border)',
            borderRadius: '8px',
            padding: '8px 12px',
            fontSize: '12px',
          }}
        >
          <p style={{ fontWeight: 600, color: 'var(--text-primary)' }}>
            {item.name}
          </p>
          <p style={{ color: 'var(--text-secondary)' }}>
            Value: {formatTooltipValue(item.value)}
          </p>
          <p style={{ color: 'var(--text-muted)' }}>{percentage}%</p>
        </div>
      );
    }
    return null;
  };

  const renderCustomizedLabel = ({
    cx,
    cy,
    midAngle,
    innerRadius,
    outerRadius,
    percent,
  }: any) => {
    if (!showPercentage || percent < 0.05) return null;
    const RADIAN = Math.PI / 180;
    const radius = innerRadius + (outerRadius - innerRadius) * 0.5;
    const x = cx + radius * Math.cos(-midAngle * RADIAN);
    const y = cy + radius * Math.sin(-midAngle * RADIAN);

    return (
      <text
        x={x}
        y={y}
        fill="white"
        textAnchor="middle"
        dominantBaseline="central"
        fontSize={11}
        fontWeight={600}
      >
        {`${(percent * 100).toFixed(0)}%`}
      </text>
    );
  };

  return (
    <div className="w-full">
      {title && (
        <h3 className="text-sm font-medium text-text-secondary mb-4">{title}</h3>
      )}
      <div className="relative">
        <ResponsiveContainer width="100%" height={height}>
          <PieChart>
            {showTooltip && <Tooltip content={<CustomTooltip />} />}
            <Pie
              data={data}
              cx="50%"
              cy="50%"
              labelLine={false}
              label={renderCustomizedLabel}
              outerRadius={height / 2 - 30}
              innerRadius={height / 3}
              dataKey="value"
              nameKey="name"
              paddingAngle={2}
            >
              {data.map((entry, index) => (
                <Cell
                  key={`cell-${index}`}
                  fill={entry.color || colors[index % colors.length]}
                  stroke="var(--background)"
                  strokeWidth={2}
                />
              ))}
            </Pie>
            {showLegend && (
              <Legend
                layout="horizontal"
                align="center"
                verticalAlign="bottom"
                wrapperStyle={{ fontSize: '11px', paddingTop: '10px' }}
                formatter={(value) => (
                  <span style={{ color: 'var(--text-primary)' }}>{value}</span>
                )}
              />
            )}
          </PieChart>
        </ResponsiveContainer>
        {centerValue && (
          <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
            {centerLabel && (
              <span className="text-xs text-text-muted">{centerLabel}</span>
            )}
            <span className="text-xl font-bold text-text-primary">
              {formatValue ? formatValue(total) : centerValue}
            </span>
          </div>
        )}
      </div>
    </div>
  );
}
